import { processMediaFileUrl } from '@edusense/entities-media';

export interface DraftLectureResource {
  id?: string | null;
  title?: string | null;
  url?: string | null;
  size?: number | null;
  fileSize?: number | null;
  mimeType?: string | null;
  createdAt?: string | null;
  uploadedAt?: string | null;
}

export interface ResourceFile {
  id: string;
  name: string;
  url: string;
  size?: number | null;
  mimeType?: string | null;
  uploadedAt?: string | null;
  sectionTitle?: string | null;
  lectureTitle?: string | null;
}

export interface CourseContentPayload {
  sections?: SectionPayload[] | null;
}

interface SectionPayload {
  title?: string | null;
  lectures?: LecturePayload[] | null;
}

interface LecturePayload {
  id?: string | null;
  title?: string | null;
  resources?: DraftLectureResource[] | null;
}

export interface CourseAssetsPayload {
  content?: CourseAssetSummary[] | null;
}

export interface CourseAssetSummary {
  id?: number | string | null;
  title?: string | null;
  fileName?: string | null;
  assetType?: string | null;
  fileSize?: number | null;
  downloadUrl?: string | null;
  uploadedAt?: string | null;
}

export const STORAGE_PREFIX = 'course_resources:';

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
};

export const humanFileSize = (bytes?: number | null): string | null => {
  if (!bytes || Number.isNaN(bytes)) {
    return null;
  }

  const threshold = 1024;
  if (bytes < threshold) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = -1;

  do {
    size /= threshold;
    unitIndex += 1;
  } while (size >= threshold && unitIndex < units.length - 1);

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

export const toResourceItem = (
  resource: DraftLectureResource,
  context: { sectionTitle?: string | null; lectureTitle?: string | null; lectureId?: string | null },
): ResourceFile | null => {
  const resourceUrl = resource.url ?? '';
  if (!resourceUrl) {
    return null;
  }

  const identifier = resource.id ?? `${context.lectureId ?? 'lecture'}:${resource.title ?? resourceUrl}`;

  return {
    id: identifier,
    name: resource.title || resource.url || '이름 없는 자료',
    url: resourceUrl,
    size: resource.size ?? resource.fileSize ?? null,
    mimeType: resource.mimeType ?? null,
    uploadedAt: resource.createdAt ?? resource.uploadedAt ?? null,
    sectionTitle: context.sectionTitle ?? null,
    lectureTitle: context.lectureTitle ?? null,
  };
};

export const mergeResources = (primary: ResourceFile[], secondary: ResourceFile[]): ResourceFile[] => {
  const map = new Map<string, ResourceFile>();

  const upsert = (entry: ResourceFile) => {
    const key = entry.id || entry.url;
    const existing = map.get(key);

    if (!existing) {
      map.set(key, entry);
      return;
    }

    map.set(key, {
      ...existing,
      ...entry,
      sectionTitle: entry.sectionTitle ?? existing.sectionTitle,
      lectureTitle: entry.lectureTitle ?? existing.lectureTitle,
    });
  };

  secondary.forEach(upsert);
  primary.forEach(upsert);

  return Array.from(map.values()).sort((a, b) => {
    const timeA = a.uploadedAt ? Date.parse(a.uploadedAt) : Number.NEGATIVE_INFINITY;
    const timeB = b.uploadedAt ? Date.parse(b.uploadedAt) : Number.NEGATIVE_INFINITY;

    if (Number.isFinite(timeA) && Number.isFinite(timeB) && timeA !== timeB) {
      return timeB - timeA;
    }

    if (Number.isFinite(timeA) && !Number.isFinite(timeB)) {
      return -1;
    }

    if (!Number.isFinite(timeA) && Number.isFinite(timeB)) {
      return 1;
    }

    return a.name.localeCompare(b.name);
  });
};

export const getStoredResources = (courseId: string): ResourceFile[] => {
  const storage = getStorage();
  if (!storage) {
    return [];
  }
  try {
    const raw = storage.getItem(`${STORAGE_PREFIX}${courseId}`);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is ResourceFile => Boolean(item && typeof item.url === 'string'));
    }
  } catch (error) {
    console.warn('[ResourcesTab] Failed to parse stored resources', error);
  }
  return [];
};

export const persistResources = (courseId: string, resources: ResourceFile[]) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(`${STORAGE_PREFIX}${courseId}`, JSON.stringify(resources));
  } catch (error) {
    console.warn('[ResourcesTab] Failed to persist resources', error);
  }
};

const isCourseContentPayload = (payload: unknown): payload is CourseContentPayload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const candidateSections = (payload as CourseContentPayload).sections;
  return candidateSections === undefined || Array.isArray(candidateSections);
};

export const extractResourcesFromPayload = (payload: unknown): ResourceFile[] => {
  if (!isCourseContentPayload(payload)) {
    return [];
  }

  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const collected: ResourceFile[] = [];

  sections.forEach(section => {
    const sectionTitle = section?.title ?? null;
    const lectures = Array.isArray(section?.lectures) ? section?.lectures ?? [] : [];

    lectures.forEach(lecture => {
      const lectureTitle = lecture?.title ?? null;
      const lectureId = lecture?.id ?? null;
      const resources = Array.isArray(lecture?.resources) ? lecture.resources : [];

      resources.forEach(resource => {
        const mapped = toResourceItem(resource, { sectionTitle, lectureTitle, lectureId });
        if (mapped) {
          collected.push(mapped);
        }
      });
    });
  });

  return collected;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

export const getStatusFromError = (error: unknown): number | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  const response = isRecord(error.response) ? error.response : undefined;
  const status = typeof response?.status === 'number' ? response.status : undefined;
  return status;
};

export interface ResourceAddedDetail {
  id?: string;
  name?: string;
  url: string;
  size?: number | null;
  mimeType?: string | null;
  uploadedAt?: string | null;
  sectionTitle?: string | null;
  lectureTitle?: string | null;
}

export const isResourceAddedEvent = (event: Event): event is CustomEvent<ResourceAddedDetail> => {
  if (!(event instanceof CustomEvent)) {
    return false;
  }

  const detail = event.detail;
  if (!detail || typeof detail !== 'object') {
    return false;
  }

  return typeof (detail as ResourceAddedDetail).url === 'string';
};

const isCourseAssetsPayload = (payload: unknown): payload is CourseAssetsPayload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const candidateContent = (payload as CourseAssetsPayload).content;
  return candidateContent === undefined || Array.isArray(candidateContent);
};

export const extractResourcesFromAssetsPayload = (payload: unknown): ResourceFile[] => {
  if (!isCourseAssetsPayload(payload)) {
    return [];
  }

  const assets = Array.isArray(payload.content) ? payload.content : [];

  return assets
    .map((asset) => {
      if (!asset) {
        return null;
      }

      const resolvedUrl = processMediaFileUrl(asset.downloadUrl ?? undefined) ?? asset.downloadUrl ?? '';
      if (!resolvedUrl) {
        return null;
      }

      const name = asset.title?.trim() || asset.fileName?.trim() || resolvedUrl;

      return {
        id: String(asset.id ?? resolvedUrl),
        name,
        url: resolvedUrl,
        size: asset.fileSize ?? null,
        mimeType: asset.assetType ?? null,
        uploadedAt: asset.uploadedAt ?? null,
        sectionTitle: null,
        lectureTitle: null,
      } satisfies ResourceFile;
    })
    .filter((item): item is ResourceFile => Boolean(item));
};
